import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Volume2, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import VoiceCommandHelp from './VoiceCommandHelp';

/**
 * GlobalVoiceCommand - A floating voice command button for app-wide navigation and actions
 * 
 * Supported commands:
 * - Navigation: "go to dashboard", "open players", "show schedule", etc.
 * - Actions: "add player", "create practice plan", "send message", etc.
 */
export default function GlobalVoiceCommand({ user }) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [lastCommand, setLastCommand] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [isFirstUse, setIsFirstUse] = useState(false);
  const recognitionRef = useRef(null);
  const navigate = useNavigate();

  // Voice command mappings
  const navigationCommands = {
    'dashboard': '/dashboard',
    'home': '/dashboard',
    'players': '/players',
    'roster': '/players',
    'schedule': '/schedule',
    'calendar': '/schedule',
    'practice': '/practice',
    'videos': '/videos',
    'film': '/videos',
    'photos': '/photos',
    'gallery': '/photos',
    'stats': '/stats',
    'statistics': '/stats',
    'whiteboard': '/whiteboard',
    'reports': '/reports',
    'settings': '/settings',
    'analytics': '/analytics',
    'contacts': '/contact-cards',
    'messages': '/team-chat',
    'chat': '/team-chat',
    'social': '/social-media',
    'social media': '/social-media'
  };

  const processCommand = useCallback((transcript) => {
    const command = transcript.toLowerCase().trim();
    setLastCommand(command);

    // Check for navigation commands
    for (const [key, path] of Object.entries(navigationCommands)) {
      if (command.includes(`go to ${key}`) || 
          command.includes(`open ${key}`) || 
          command.includes(`show ${key}`) ||
          command.includes(`navigate to ${key}`) ||
          command === key) {
        toast.success(`Navigating to ${key}...`);
        navigate(path);
        return true;
      }
    }

    // Check for action commands
    if (command.includes('add player') || command.includes('new player')) {
      toast.info('Opening add player dialog...');
      navigate('/players');
      setTimeout(() => {
        const addBtn = document.querySelector('[data-testid="add-player-btn"]');
        if (addBtn) addBtn.click();
      }, 500);
      return true;
    }

    if (command.includes('schedule event') || command.includes('add event') || command.includes('new event')) {
      toast.info('Opening schedule dialog...');
      navigate('/schedule');
      setTimeout(() => {
        const addBtn = document.querySelector('[data-testid="add-schedule-btn"]');
        if (addBtn) addBtn.click();
      }, 500);
      return true;
    }

    if (command.includes('create practice') || command.includes('new practice') || command.includes('practice plan')) {
      toast.info('Opening practice page...');
      navigate('/practice');
      return true;
    }

    if (command.includes('upload video') || command.includes('add video')) {
      toast.info('Opening video upload...');
      navigate('/videos');
      return true;
    }

    if (command.includes('send message') || command.includes('new message')) {
      toast.info('Opening messages...');
      navigate('/team-chat');
      return true;
    }

    if (command.includes('take attendance') || command.includes('attendance')) {
      toast.info('Opening practice attendance...');
      navigate('/practice');
      return true;
    }

    // Help command
    if (command.includes('help') || command.includes('what can you do')) {
      toast.info(
        'Voice commands: "Go to [page]", "Add player", "Schedule event", "Create practice plan", "Upload video"',
        { duration: 5000 }
      );
      return true;
    }

    // Unknown command
    toast.warning(`Command not recognized: "${command}". Say "help" for available commands.`);
    return false;
  }, [navigate]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.log('Speech Recognition not supported');
      setIsSupported(false);
      return;
    }

    setIsSupported(true);

    try {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';
      recognitionRef.current.maxAlternatives = 1;

      recognitionRef.current.onstart = () => {
        console.log('Global voice command started');
        setIsListening(true);
      };

      recognitionRef.current.onend = () => {
        console.log('Global voice command ended');
        setIsListening(false);
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        console.log('Voice command received:', transcript);
        processCommand(transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Voice command error:', event.error);
        setIsListening(false);
        
        switch (event.error) {
          case 'not-allowed':
          case 'permission-denied':
            toast.error('Please allow microphone access for voice commands');
            break;
          case 'no-speech':
            toast.info('No speech detected. Try again.');
            break;
          case 'audio-capture':
            toast.error('No microphone found');
            break;
          case 'network':
            toast.error('Network error for voice recognition');
            break;
          case 'aborted':
            // User cancelled - no message needed
            break;
          default:
            // Don't show error for service-not-allowed or other non-critical
            if (event.error !== 'service-not-allowed') {
              console.warn(`Voice command warning: ${event.error}`);
            }
        }
      };
    } catch (error) {
      console.error('Failed to initialize voice commands:', error);
      setIsSupported(false);
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [processCommand]);

  const toggleListening = () => {
    if (!isSupported) {
      toast.error('Voice commands not supported in your browser. Use Chrome or Edge.');
      return;
    }

    // Check if this is first time using voice
    const voiceUsed = localStorage.getItem('voice_command_used');
    if (!voiceUsed) {
      localStorage.setItem('voice_command_used', 'true');
      setIsFirstUse(true);
      setShowHelp(true);
      return; // Show help first, user can click mic again after
    }

    if (isListening) {
      try {
        recognitionRef.current?.stop();
      } catch (error) {
        setIsListening(false);
      }
    } else {
      try {
        // Stop any existing session first
        try {
          recognitionRef.current?.stop();
        } catch (e) {
          // Ignore
        }
        
        setTimeout(() => {
          try {
            recognitionRef.current?.start();
            toast.info('Listening... Say a command or "help"', { duration: 2000 });
          } catch (error) {
            if (!error.message?.includes('already started')) {
              console.error('Could not start voice command:', error);
              toast.error('Could not start voice command. Try again.');
            }
          }
        }, 100);
      } catch (error) {
        console.error('Voice command error:', error);
      }
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <>
      <VoiceCommandHelp 
        isOpen={showHelp} 
        onClose={() => setShowHelp(false)}
        showOnFirstUse={isFirstUse}
      />
      
      <div className="fixed bottom-24 right-6 z-40">
        {/* Help button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowHelp(true)}
          className="absolute -top-10 right-0 h-8 w-8 p-0 rounded-full bg-background/80 backdrop-blur-sm border shadow-sm hover:bg-background"
          title="Voice command help"
          data-testid="voice-help-trigger-btn"
        >
          <HelpCircle className="h-4 w-4 text-muted-foreground" />
        </Button>
        
        {/* Main mic button */}
        <Button
          onClick={toggleListening}
          size="lg"
          className={`rounded-full w-14 h-14 shadow-lg transition-all ${
            isListening 
              ? 'bg-red-500 hover:bg-red-600 animate-pulse scale-110' 
              : 'bg-primary hover:bg-primary/90'
          }`}
          title={isListening ? 'Stop listening' : 'Voice command (say "help" for options)'}
          data-testid="global-voice-command-btn"
        >
          {isListening ? (
            <MicOff className="h-6 w-6" />
          ) : (
            <Mic className="h-6 w-6" />
          )}
        </Button>
        {isListening && (
          <div className="absolute -top-12 right-0 bg-background border rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
            <div className="flex items-center gap-2 text-sm">
              <Volume2 className="h-4 w-4 animate-pulse text-primary" />
              <span>Listening...</span>
            </div>
          </div>
        )}
        {lastCommand && !isListening && (
          <div className="absolute -top-10 right-14 bg-muted rounded-lg px-3 py-1 shadow text-xs max-w-[200px] truncate">
            "{lastCommand}"
          </div>
        )}
      </div>
    </>
  );
}
